import axios from 'axios';

const API_BASE_URL = 'https://api.scripture.api.bible/v1';
const API_KEY = import.meta.env.BIBLE_API_KEY;

if (!API_KEY) {
  console.error('Bible API key not found. Please set BIBLE_API_KEY in your environment variables.');
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'api-key': API_KEY
  },
  timeout: 10000 // 10 seconds timeout
});

export interface Translation {
  id: string;
  name: string;
  language: {
    id: string;
    name: string;
  };
}

export interface Book {
  id: string;
  name: string;
  nameLong: string;
  chapters: {
    id: string;
    number: string;
    reference: string;
  }[];
}

export interface Chapter {
  id: string;
  number: string;
  reference: string;
  content: string;
  verses: {
    id: string;
    number: string;
    text: string;
  }[];
}

// Fetch available translations
export async function getAvailableTranslations(): Promise<Translation[]> {
  try {
    const response = await api.get('/bibles');
    
    // Filter translations to only include Portuguese versions
    const allTranslations = response.data.data;
    const portugueseTranslations = allTranslations.filter((translation: Translation) => 
      translation.language.name === "Portuguese" || translation.language.id === "por"
    );
    
    return portugueseTranslations;
  } catch (error) {
    console.error('Error fetching translations:', error);
    
    // Return fallback data if API fails
    return [
      {
        id: 'acf',
        name: 'Almeida Corrigida Fiel',
        language: { id: 'por', name: 'Portuguese' }
      },
      {
        id: 'ara',
        name: 'Almeida Revista e Atualizada',
        language: { id: 'por', name: 'Portuguese' }
      },
      {
        id: 'nvi',
        name: 'Nova Versão Internacional',
        language: { id: 'por', name: 'Portuguese' }
      },
      {
        id: 'ntlh',
        name: 'Nova Tradução na Linguagem de Hoje',
        language: { id: 'por', name: 'Portuguese' }
      }
    ];
  }
}

// Fetch books for a specific translation
export async function getBooksForTranslation(translationId: string): Promise<Book[]> {
  try {
    const response = await api.get(`/bibles/${translationId}/books`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching books:', error);
    
    // Check if it's a 500 error or other server error
    if (axios.isAxiosError(error) && error.response && error.response.status >= 500) {
      console.warn('Server error, returning fallback data');
      // Return fallback data - simplified list of books
      return [
        { id: 'GEN', name: 'Gênesis', nameLong: 'Gênesis', chapters: [] },
        { id: 'EXO', name: 'Êxodo', nameLong: 'Êxodo', chapters: [] },
        { id: 'PSA', name: 'Salmos', nameLong: 'Salmos', chapters: [] },
        { id: 'MAT', name: 'Mateus', nameLong: 'Mateus', chapters: [] },
        { id: 'JHN', name: 'João', nameLong: 'João', chapters: [] },
        { id: 'ROM', name: 'Romanos', nameLong: 'Romanos', chapters: [] }
      ];
    }
    
    throw new Error('Não foi possível carregar os livros desta tradução. Por favor, tente novamente mais tarde.');
  }
}

// Fetch chapters for a specific book
export async function getChaptersForBook(translationId: string, bookId: string): Promise<Chapter[]> {
  try {
    const response = await api.get(`/bibles/${translationId}/books/${bookId}/chapters`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching chapters:', error);
    
    // Check if it's a server error
    if (axios.isAxiosError(error) && error.response && error.response.status >= 500) {
      console.warn('Server error, returning fallback data');
      // Return fallback data - 5 chapters
      return Array.from({ length: 5 }, (_, i) => ({
        id: `${bookId}.${i+1}`,
        number: `${i+1}`,
        reference: `Capítulo ${i+1}`
      }));
    }
    
    throw new Error('Não foi possível carregar os capítulos deste livro. Por favor, tente novamente mais tarde.');
  }
}

// Fetch chapter content
export async function getChapterContent(translationId: string, chapterId: string): Promise<Chapter> {
  try {
    const response = await api.get(`/bibles/${translationId}/chapters/${chapterId}`, {
      params: {
        'content-type': 'json',
        'include-notes': false,
        'include-titles': false,
        'include-chapter-numbers': false,
        'include-verse-numbers': true,
        'include-verse-spans': false
      }
    });
    
    // Parse the content to extract verses
    const chapter = response.data.data;
    const content = chapter.content;
    
    // Extract verses using regex
    const verseRegex = /<verse number="(\d+)"[^>]*>(.*?)<\/verse>/g;
    const verses = [];
    let match;
    
    while ((match = verseRegex.exec(content)) !== null) {
      verses.push({
        id: `${chapter.id}_${match[1]}`,
        number: match[1],
        text: match[2].replace(/<[^>]*>/g, '').trim()
      });
    }
    
    return {
      ...chapter,
      verses
    };
  } catch (error) {
    console.error('Error fetching chapter content:', error);
    
    // Check if it's a server error
    if (axios.isAxiosError(error) && error.response && error.response.status >= 500) {
      console.warn('Server error, returning fallback data');
      // Return fallback data with sample verses
      return {
        id: chapterId,
        number: chapterId.split('.').pop() || '1',
        reference: `Capítulo ${chapterId.split('.').pop() || '1'}`,
        content: '',
        verses: Array.from({ length: 10 }, (_, i) => ({
          id: `${chapterId}_${i+1}`,
          number: `${i+1}`,
          text: `Este é um texto de exemplo para o versículo ${i+1}. O serviço de API está temporariamente indisponível.`
        }))
      };
    }
    
    throw new Error('Não foi possível carregar o texto deste capítulo. Por favor, tente novamente mais tarde.');
  }
}

// Search the Bible
export async function searchBible(translationId: string, query: string): Promise<any> {
  try {
    const response = await api.get(`/bibles/${translationId}/search`, {
      params: {
        query,
        limit: 50
      }
    });
    return response.data.data;
  } catch (error) {
    console.error('Error searching Bible:', error);
    
    // Check if it's a server error
    if (axios.isAxiosError(error) && error.response && error.response.status >= 500) {
      console.warn('Server error, returning fallback data');
      // Return fallback data with empty results
      return { verses: [] };
    }
    
    throw new Error('Não foi possível realizar a busca. Por favor, tente novamente mais tarde.');
  }
}

// Function to fetch Bible text (used by import script)
export async function fetchBibleText(bookId: string, chapter: number, translation: string): Promise<any[]> {
  try {
    // This is a simplified implementation
    // In a real app, you would fetch from an actual API
    console.log(`Fetching ${bookId} ${chapter} in ${translation}`);
    
    // Return mock data for testing
    return Array.from({ length: 30 }, (_, i) => ({
      book_id: bookId,
      chapter: chapter,
      verse: i + 1,
      text: `This is verse ${i + 1} of chapter ${chapter} in ${bookId}.`,
      translation: translation
    }));
  } catch (error) {
    console.error('Error fetching Bible text:', error);
    throw error;
  }
}